import { Box } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import type { ChangeEvent, FC } from "react"
import { useState } from "react"

import { useInventoryAdjustmentHistoryFilterOptions } from "../hooks/useInventoryAdjustmentHistoryFilterOptions"
import { useInventoryAdjustmentHistoryList } from "../hooks/useInventoryAdjustmentHistoryList"

import { InventoryAdjustmentHistoryDocumentsDialog } from "./InventoryAdjustmentHistoryDocumentsDialog"
import { InventoryAdjustmentHistoryFilterChips } from "./InventoryAdjustmentHistoryFilterChips"
import { InventoryAdjustmentHistoryFilterDrawer } from "./InventoryAdjustmentHistoryFilterDrawer"
import { InventoryAdjustmentHistoryTable } from "./InventoryAdjustmentHistoryTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"

export const InventoryAdjustmentHistoryListPage: FC = () => {
  const [documentsItem, setDocumentsItem] = useState<AdjustmentHistoryItem | undefined>()
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const {
    inventoryAdjustmentHistoryItems,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleScroll,
  } = useInventoryAdjustmentHistoryList()

  const { adjustmentTypeOptions, statusOptions, reasonOptions, warehouseOptions, adjustedByOptions } =
    useInventoryAdjustmentHistoryFilterOptions()

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by Product ID, Lot #, Pallet #, Adjustment #, or Description'
          defaultValue={searchParams.query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          className='w-full max-w-[524px]'
          data-testid='adjustment-history.list-page.search-field'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={() => setIsFilterDrawerOpen(true)}
          data-testid='adjustment-history.list-page.filter-button'
        >
          Filter
        </Button>
      </Box>

      <InventoryAdjustmentHistoryFilterChips
        searchParams={searchParams}
        adjustmentTypeOptions={adjustmentTypeOptions}
        statusOptions={statusOptions}
        reasonOptions={reasonOptions}
        warehouseOptions={warehouseOptions}
        adjustedByOptions={adjustedByOptions}
        onRemove={handleFilterRemove}
      />

      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <InventoryAdjustmentHistoryTable
          data={inventoryAdjustmentHistoryItems}
          loading={isFetching}
          error={error}
          sorting={sorting}
          containerRef={tableContainerRef}
          onSortingChange={handleSortingChange}
          onScroll={handleScroll}
          onDocumentsOpen={setDocumentsItem}
        />
      </Box>

      <InventoryAdjustmentHistoryFilterDrawer
        open={isFilterDrawerOpen}
        searchParams={searchParams}
        adjustmentTypeOptions={adjustmentTypeOptions}
        statusOptions={statusOptions}
        reasonOptions={reasonOptions}
        warehouseOptions={warehouseOptions}
        adjustedByOptions={adjustedByOptions}
        onToggle={() => setIsFilterDrawerOpen(false)}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />

      {documentsItem && (
        <InventoryAdjustmentHistoryDocumentsDialog item={documentsItem} onClose={() => setDocumentsItem(undefined)} />
      )}
    </Box>
  )
}
